import { useCallback, useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import {
  ListLoading,
  formatUnknownErrorMessage,
  useToast,
} from '@gbs-monorepo-packages/common';
import { zodResolver } from '@hookform/resolvers/zod';

import { FormLayout } from '../../components/FormLayout';
import { DEFAULT_TERMS_DOCUMENT_PATH } from '../../constants';
import { type IRedirectOptions } from '../../contexts';
import { useAuth } from '../../hooks/useAuth';
import {
  Checkbox,
  CheckboxLabel,
  CheckboxWrapper,
  Description,
  ErrorMessage,
  Form,
  LazyImageCustom,
  SubmitButton,
  TermsLink,
  TextContent,
  Title,
} from './styles';

const acceptTermsSchema = z.object({
  accepted: z.literal(true, {
    errorMap: () => ({
      message: 'You must accept the Terms and Conditions to continue.',
    }),
  }),
});

type AcceptTermsFormValues = z.infer<typeof acceptTermsSchema>;

interface ILocationState {
  from?: string;
}

export const AcceptTerms = (): JSX.Element => {
  const [loading, setLoading] = useState(false);
  const [syncingUser, setSyncingUser] = useState(true);
  const [refreshSucceeded, setRefreshSucceeded] = useState(false);
  const {
    acceptTerms,
    latestTermsVersion,
    refreshUserFromServer,
    user,
    mainContentPath,
    termsAcceptancePath,
  } = useAuth();
  const { addToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const from = (location.state as ILocationState | null)?.from;

  const termsUrl = DEFAULT_TERMS_DOCUMENT_PATH as string;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AcceptTermsFormValues>({
    resolver: zodResolver(acceptTermsSchema),
  });

  useEffect(() => {
    let isMounted = true;
    const sync = async () => {
      const ok = await refreshUserFromServer();
      if (isMounted) {
        setRefreshSucceeded(ok);
        setSyncingUser(false);
      }
    };
    void sync();
    return () => {
      isMounted = false;
    };
  }, [refreshUserFromServer]);

  useEffect(() => {
    if (syncingUser || !refreshSucceeded || !user) return;
    if (latestTermsVersion === 0) return;
    if ((user.currentTermsVersionAccepted ?? 0) < latestTermsVersion) return;
    const acceptPath = termsAcceptancePath ?? '';
    const target =
      from && (!acceptPath || from !== acceptPath) ? from : mainContentPath;
    navigate(target, { replace: true });
  }, [
    syncingUser,
    refreshSucceeded,
    user,
    latestTermsVersion,
    from,
    mainContentPath,
    termsAcceptancePath,
    navigate,
  ]);

  const onSubmit: SubmitHandler<AcceptTermsFormValues> =
    useCallback(async () => {
      if (loading) return;
      setLoading(true);

      const redirect: IRedirectOptions | undefined = from
        ? { to: from, replace: true }
        : undefined;

      await acceptTerms(latestTermsVersion, redirect).catch(
        (error: unknown) => {
          addToast({
            title: 'Failed to accept Terms and Conditions',
            description: formatUnknownErrorMessage(error),
            styleType: 'error',
            dataCy: 'accept-terms-error-toast',
          });
        }
      );

      setLoading(false);
    }, [acceptTerms, latestTermsVersion, from, addToast]);

  return (
    <FormLayout dataCy="accept-terms-container">
      {syncingUser ? (
        <ListLoading />
      ) : (
        <>
          <TextContent>
            <LazyImageCustom
              src="/logo.png"
              alt="GBS Benefits logo"
              data-cy="accept-terms-logo"
            />
            <Title data-cy="accept-terms-title">Terms & Conditions</Title>
            <Description data-cy="accept-terms-description">
              To continue using our platform, please review and accept the Terms
              and Conditions.
            </Description>
          </TextContent>

          <Form onSubmit={handleSubmit(onSubmit)} data-cy="accept-terms-form">
            <TextContent>
              <TermsLink
                href={termsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-cy="terms-document-link"
              >
                Read the Terms and Conditions
              </TermsLink>
            </TextContent>
            <CheckboxWrapper>
              <Checkbox
                id="accepted"
                type="checkbox"
                data-cy="checkbox-accept-terms"
                {...register('accepted')}
              />
              <CheckboxLabel
                htmlFor="accepted"
                data-cy="accept-terms-checkbox-label"
              >
                I have read and agree to the Terms and Conditions.
              </CheckboxLabel>
            </CheckboxWrapper>

            {errors.accepted && (
              <ErrorMessage data-cy="accept-terms-checkbox-error">
                {errors.accepted.message}
              </ErrorMessage>
            )}

            <SubmitButton
              type="submit"
              loading={loading || syncingUser}
              disabled={latestTermsVersion === 0 || syncingUser}
              dataCy="button-accept-terms-submit"
            >
              Accept and Continue
            </SubmitButton>
          </Form>
        </>
      )}
    </FormLayout>
  );
};
